import Cookies from 'js-cookie';
import axios from 'axios';
import { CheckUserAuthorizationResponse } from '@blisspointmedia/bpm-types';
import { SetError } from '@src/redux/modals-types';
import { awaitJSON, UsersLambdaFetch } from '../fetch-utils';
import {
  NotAuthorizedError,
  NotSignedInError,
  signOut as authClientSignOut,
  getTokens
} from './auth-client';
import {
  ACCESS_TOKEN_LIFE,
  ACCESS_TOKEN_LIFE_DELTA,
  AuthCookies,
  GOOGLE_CLIENT_ID
} from './auth-constants';
import { decodeJwtEmail } from './access-denied';
import { removeCookiesAllScopes } from './legacy-purge';
import {
  authCookieOptions,
  captureIntendedDestination,
  loginRedirect,
  oauthRedirectInProgress,
  refreshCognitoToken
} from './session';

// Re-exported for existing importers — the implementations live in ./session so that
// fetch-utils.ts (which needs refreshCognitoToken/loginRedirect for recoverable-auth-error
// handling) can import them without an import cycle back through this file's own
// UsersLambdaFetch/awaitJSON dependency on fetch-utils.ts.
export { captureIntendedDestination, loginRedirect, oauthRedirectInProgress, refreshCognitoToken };

// Runs the same pre-check today's password sign-in has always run before showing the password
// field: confirms the email is a known, authorized account. Kept as a separate call (not folded
// into POST /auth/password) per plan D4 — it stays a repair seam ADR-007 Decision 7 depends on.
// Messaging matches the pre-PLATFORM-988 behavior exactly.
export const checkUserAuthorization = async (email: string): Promise<void> => {
  const res = await UsersLambdaFetch('/checkUserAuthorization', {
    method: 'POST',
    mode: 'cors', // no-cors, *cors, same-origin
    body: JSON.stringify({ email }),
    headers: { 'Content-Type': 'application/json' },
    // No session exists yet here (mid-login) — a 401 means "email not authorized," not a dead
    // token, so it shouldn't go through BpmLambdaFetch's recoverable-auth-error handling.
    skipAuthRecovery: true,
    // The endpoint is `auth: false`. Without this, buildAuthHeader() calls getTokens(), which throws
    // NotSignedInError with no session, so the password pre-check failed with "You've been logged
    // out" before any request was sent.
    authenticate: false
  });
  const verificationResponse = await awaitJSON<CheckUserAuthorizationResponse>(res);
  // Only an explicit `false` is a definitive deny (NotAuthorizedError, same message the password
  // pre-check has always shown). Anything else that isn't `true` is a malformed answer, not a deny.
  if (verificationResponse.userAuthorized === false) {
    throw new NotAuthorizedError("This username doesn't exist");
  }
  if (verificationResponse.userAuthorized !== true) {
    throw new Error('Could not verify this account. Please try again.');
  }
};

// Silently requests a Google API access token via the GSI token client (no auth prompt if the
// user already granted Drive consent for this account) and stores it in the
// google_api_access_token cookie. Pass a broader scope for Drive/Slides access — GSI will prompt
// for consent if not yet granted. Independent of how the user signed in to the app (password,
// Google or JumpCloud) — see checkGoogleAccessTokens.
export const ensureGoogleApiAccessToken = (
  scope = 'profile email openid https://www.googleapis.com/auth/drive',
  prompt = '',
  // When provided, GSI pre-selects this Google account and skips the account chooser.
  hint?: string
): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!window.google) {
      reject(new Error('Google identity services not available'));
      return;
    }
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope,
      auto_select: true,
      ...(hint ? { hint } : {}),
      callback: response => {
        if (response.access_token) {
          Cookies.set(
            AuthCookies.GOOGLE_API_ACCESS_TOKEN,
            response.access_token,
            authCookieOptions()
          );
          Cookies.set(
            AuthCookies.GOOGLE_API_ACCESS_TOKEN_EXPIRATION,
            String(Date.now() + ACCESS_TOKEN_LIFE - ACCESS_TOKEN_LIFE_DELTA),
            authCookieOptions()
          );
          resolve(response.access_token);
        } else {
          reject(new Error('No access token in Google response'));
        }
      }
    });
    client.requestAccessToken({ prompt });
  });

// Signs the user out (revokes the refresh token server-side and clears in-memory tokens via
// auth-client, then navigates to the IdP's logout URL for a federated session) and clears all
// local auth state. Resolves once sign-out is complete so the caller can then switch to the
// login view. Intentionally does NOT reload the page itself: the caller flips auth state in
// React instead, so logout is a soft transition with no full-page "blink" for a password
// session (a federated session's authClient.signOut() navigates away on its own, and this
// resolves true). This is the only way to end a session: clearAuthCookies() alone removes legacy
// readable cookies, and can't touch the HttpOnly refresh cookie or revoke its token.
export const signOutAWS = async (): Promise<boolean> => {
  let navigatingAway = false;
  try {
    navigatingAway = await authClientSignOut();
  } catch (e) {
    console.error('error signing out: ', e);
  } finally {
    sessionStorage.clear();
    clearAuthCookies();
  }
  return navigatingAway;
};

export const clearAuthCookies = () => removeCookiesAllScopes(Object.values(AuthCookies));

export const clearRefreshToken = () => {
  try {
    window.google?.accounts.oauth2.revoke();
  } finally {
    Cookies.remove(AuthCookies.GOOGLE_REFRESH_TOKEN);
  }
};

export const periodicallyRefreshGoogleOrCognito = async () => {
  try {
    await refreshCognitoToken();
  } catch (e) {
    console.error('Failed to refresh Cognito auth.', e);
  }
};

export const initPeriodicRefresh = () => {
  periodicallyRefreshGoogleOrCognito();
};

// Resolves true once the current in-memory/refresh-cookie session is confirmed, false when there
// is no session (NotSignedInError). Any other failure (RefreshUnavailableError: the refresh
// endpoint 5xx'd, timed out or returned garbage) is rethrown UNCHANGED: it says nothing about
// whether a session exists, so callers must never reload or clear session state because of it.
export const checkAuth = async (): Promise<boolean> => {
  try {
    await getTokens();
    return true;
  } catch (e) {
    if (e instanceof NotSignedInError) return false;
    throw e;
  }
};

export const getGoogleRefreshToken = () => Cookies.get(AuthCookies.GOOGLE_REFRESH_TOKEN);

// Passive check for whether a Google API access-token cookie is present. Used to gate UI (e.g.
// Slide Generator's action buttons) WITHOUT triggering GSI's popup — the popup must be opened from
// a user gesture or the browser's popup blocker eats it. Acquisition happens on a real click via
// checkGoogleAccessTokens; this just reflects the current cookie state.
export const hasGoogleApiAccessToken = () =>
  Boolean(Cookies.get(AuthCookies.GOOGLE_API_ACCESS_TOKEN));

// The Drive/Slides consent gate. Independent of how the user signed in to the app (PLATFORM-988
// ADR-006 Decision 3 fix: login_method is informational only and must never gate authorization).
// Any signed-in user — password, Google or JumpCloud — can grant Drive access here; GIS handles
// picking/consenting the Google account, separately from Cognito's app sign-in.
export const checkGoogleAccessTokens = async (setError: SetError): Promise<string> => {
  // Hint GSI toward the signed-in user's Google account. Without it, auto_select can mint the token
  // for another account signed in to the browser, which then 403s on the blueprint templates.
  // Best-effort: a failed token read falls back to minting without a hint.
  const getAccountHint = async (): Promise<string | undefined> => {
    try {
      return decodeJwtEmail((await getTokens()).idToken) ?? undefined;
    } catch {
      return undefined;
    }
  };

  // Re-mint a fresh Google API token with the Drive scope, prompting for consent. Runs from the
  // caller's user gesture (the generate click), so GSI's popup is allowed to open.
  const reconsent = async (): Promise<string> => {
    await ensureGoogleApiAccessToken(
      'profile email openid https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.readonly',
      'consent',
      await getAccountHint()
    );
    return Cookies.get(AuthCookies.GOOGLE_API_ACCESS_TOKEN) ?? '';
  };

  // The google_api_access_token cookie persists 24h, but the Google token behind it is only valid
  // ~1h. Treat a missing OR expired token (per its expiration cookie) as needing a fresh mint —
  // otherwise the tokeninfo check below hits Google with a dead token and gets back a 400.
  const expiration = Cookies.get(AuthCookies.GOOGLE_API_ACCESS_TOKEN_EXPIRATION);
  const tokenExpired = expiration != null && Date.now() > Number(expiration);
  const googleAccessToken = Cookies.get(AuthCookies.GOOGLE_API_ACCESS_TOKEN);

  if (!googleAccessToken || tokenExpired) {
    try {
      await ensureGoogleApiAccessToken(undefined, undefined, await getAccountHint());
      return Cookies.get(AuthCookies.GOOGLE_API_ACCESS_TOKEN) ?? '';
    } catch (e) {
      setError({
        message: e.message,
        reportError: e
      });
      return '';
    }
  }

  // Confirm Google still accepts the stored token and that it carries the Drive scope. Guard the
  // call: a token Google rejects (400 — expired/revoked) or one missing the scope re-mints from this
  // user gesture rather than throwing a raw 400 out to the caller.
  try {
    const scopeResponse = await axios.get(
      `https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${googleAccessToken}`
    );
    if (
      scopeResponse?.data?.scope &&
      !scopeResponse.data.scope.includes('https://www.googleapis.com/auth/drive.file') &&
      window.google
    ) {
      return await reconsent();
    }
    return googleAccessToken;
  } catch (e) {
    if (!window.google) {
      setError({
        message: e.message,
        reportError: e
      });
      return '';
    }
    try {
      return await reconsent();
    } catch (mintErr) {
      setError({
        message: mintErr.message,
        reportError: mintErr
      });
      return '';
    }
  }
};
